import { computed, getCurrentScope, onScopeDispose, readonly, ref, watch } from 'vue'
import { getUserSettings, updateUserSettings } from '../../services/user'
import { tallyOutcomes, useCapturedLeads } from './useCapturedLeads'
import { useMicPermission, useMicReading } from './useMicPermission'
import { useTelephony } from './useTelephony'

export type OnboardingStage = 'intro' | 'mic' | 'phone' | 'select' | 'done' | 'closed'

export type OnboardingVerdict = 'undecided' | 'onboarding' | 'queue'
const VERDICT_RANK: Record<OnboardingVerdict, number> = { undecided: 0, onboarding: 1, queue: 2 }

export interface QueuedCompany {
  domain: string
  name: string | null
}

// What the queue held when the rep arrived. Decided by the page from the loaded
// queue and the ?selected row, never from where the rep came from.
export type QueueState =
  | {
      kind: 'person'
      contactId: string
      firstName: string
      name: string
      detail: string
      phone: string
      company: QueuedCompany | null
    }
  | { kind: 'people'; count: number; companies: readonly QueuedCompany[]; dial: PeopleDial }
  | { kind: 'empty' }

// Who the done button dials: the whole queue, or only the people a link named.
export type PeopleDial = 'queue' | { contactIds: readonly string[] }

const ONBOARDED_KEY = 'dialer_onboarded_at'
// A verified phone stays on screen this long before the next step takes over.
export const PHONE_HOLD_MS = 900

async function readOnboarded(): Promise<boolean> {
  try {
    return !!(await getUserSettings())[ONBOARDED_KEY]
  } catch (e) {
    // A failed read must not hold the dialer back; the fallback is the browser
    // asking for the mic on the first call.
    console.warn('[useDialerOnboarding] reading onboarding state failed', e)
    return true
  }
}

export function useDialerOnboarding() {
  const telephony = useTelephony()
  const mic = useMicPermission()

  const verdict = ref<OnboardingVerdict>('undecided')
  // Latched for the visit: deep links decide against this, not the verdict, which
  // moves on the moment the rep finishes.
  const shown = ref(false)
  const stage = ref<OnboardingStage>('closed')
  const queueState = ref<QueueState>({ kind: 'empty' })
  const picker = useCapturedLeads()
  const phoneSkipped = ref(false)
  const micAllowedEarlier = ref(false)
  const added = ref(0)
  let hasSelectStep = false
  let stamped = false

  const micRead = computed(() => mic.state.value === 'granted' && mic.device.value !== null)
  // Also a verification that just finished, in case the projection refresh after it failed.
  const callbackNumber = computed<string | null>(() => {
    const cb = telephony.projection.value?.me.callback_number
    if (cb?.status === 'verified') return cb.phone_number
    const justVerified = telephony.verify.phase === 'verified' && telephony.verify.target === 'own'
    return justVerified ? telephony.verify.phoneNumber : null
  })
  const phoneVerified = computed(() => callbackNumber.value !== null)
  // The verdict's only writer.
  function settle(next: OnboardingVerdict): void {
    if (VERDICT_RANK[next] <= VERDICT_RANK[verdict.value]) return
    verdict.value = next
    if (next === 'onboarding') shown.value = true
  }

  let hold: ReturnType<typeof setTimeout> | null = null
  function clearHold(): void {
    if (hold) clearTimeout(hold)
    hold = null
  }
  function holdThen(ms: number, fn: () => void): void {
    clearHold()
    hold = setTimeout(() => {
      hold = null
      fn()
    }, ms)
  }

  function afterPhone(): OnboardingStage {
    return hasSelectStep ? 'select' : 'done'
  }

  function stamp(): void {
    if (stamped) return
    stamped = true
    updateUserSettings({ [ONBOARDED_KEY]: new Date().toISOString() }).catch((e) => {
      console.warn('[useDialerOnboarding] saving onboarding state failed', e)
    })
  }

  // The mic step asks again when the browser can answer (a reset in its site panel), and keeps
  // the named mic current while it shows.
  useMicReading(computed(() => stage.value === 'mic'), { ask: true })
  if (getCurrentScope()) onScopeDispose(clearHold)

  // The success beat: the verified phone shows its check, then hands over. The step is only
  // entered unverified, so this always sees the change.
  watch(phoneVerified, (verified) => {
    if (!verified || stage.value !== 'phone') return
    holdThen(PHONE_HOLD_MS, () => {
      if (stage.value === 'phone') stage.value = afterPhone()
    })
  })
  // Sync: the flag is written in the same tick the rep reaches the done page, so a
  // reload a moment later lands on the queue.
  watch(
    stage,
    (s) => {
      if (s === 'done') stamp()
    },
    { flush: 'sync' },
  )

  // Phase one, at mount: the reads that need nothing from the queue run alongside
  // it, and `telephony.ensure()` joins the page's own in-flight call rather than
  // posting a second one later. An onboarded rep is owed nothing more: the queue
  // is theirs the moment the settings land.
  let prepared: Promise<boolean> | null = null
  function init(): void {
    if (prepared) return
    const onboarded = readOnboarded()
    void onboarded.then((isOnboarded) => {
      if (isOnboarded) settle('queue')
    })
    prepared = Promise.all([onboarded, mic.init(), telephony.ensure()]).then(([isOnboarded]) => isOnboarded)
  }

  // Phase two, once the queue has loaded: what the rep came for, and whether step 3 is owed.
  async function decide(input: { queueState: QueueState; isDialerOnly: boolean }): Promise<void> {
    if (verdict.value !== 'undecided') return
    init()
    queueState.value = input.queueState
    const wantsLeads = input.isDialerOnly && input.queueState.kind === 'empty'
    const isOnboarded = await prepared!
    if (isOnboarded) return
    // No captures to offer, or a failed read, is a two-step flow, not a blocked one.
    if (wantsLeads) await picker.load()
    hasSelectStep = picker.leads.value.length > 0
    // Shown even when every step is satisfied.
    stage.value = 'intro'
    settle('onboarding')
  }

  function start(): void {
    if (stage.value !== 'intro') return
    micAllowedEarlier.value = mic.permission.value === 'granted'
    stage.value = 'mic'
    // Inside the rep's click, so every browser shows its prompt; an allowed mic answers without one.
    void mic.request()
  }

  // The only way into the phone step, so a verified phone is never asked for.
  function continueFromMic(): void {
    if (stage.value !== 'mic' || !micRead.value) return
    stage.value = phoneVerified.value ? afterPhone() : 'phone'
  }

  function skipPhone(): void {
    if (stage.value !== 'phone') return
    clearHold()
    phoneSkipped.value = true
    stage.value = afterPhone()
  }

  async function addSelected(): Promise<void> {
    if (stage.value !== 'select') return
    const results = await picker.add()
    if (!results) return
    const tally = tallyOutcomes(results)
    added.value = tally.added + tally.already
    stage.value = 'done'
  }

  function skipSelect(): void {
    if (stage.value !== 'select') return
    added.value = 0
    stage.value = 'done'
  }

  function finish(): void {
    if (stage.value !== 'done') return
    stage.value = 'closed'
    settle('queue')
  }

  return {
    verdict: readonly(verdict),
    shown: readonly(shown),
    stage: readonly(stage),
    queueState: readonly(queueState),
    leads: picker.leads,
    selectedCount: picker.selectedCount,
    allSelected: picker.allSelected,
    added: readonly(added),
    adding: picker.adding,
    addError: picker.addError,
    phoneSkipped: readonly(phoneSkipped),
    micAllowedEarlier: readonly(micAllowedEarlier),
    callbackNumber,
    init,
    decide,
    start,
    continueFromMic,
    skipPhone,
    toggleLead: picker.toggleLead,
    toggleAll: picker.toggleAll,
    addSelected,
    skipSelect,
    finish,
  }
}
